import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { ApiError, queryClient } from '@/shared/api'
import { routes } from '@/shared/config'
import { cn, hasMeaningfulHtml } from '@/shared/lib'
import { HtmlContent } from '@/shared/ui'
import { VideoCard } from '@/entities/video'
import { VideosShell } from '@/widgets/videos-shell'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import {
  applyBookmarkToCatalogCache,
  captureCatalogCacheSnapshots,
  isCatalogListQueryKey,
  restoreCatalogCacheSnapshots,
} from '../model/catalog-bookmarks-cache'
import {
  fetchCatalogCollectionDetail,
  fetchCatalogVideoDetail,
  setCatalogFavorite,
  setCatalogPlaybackProgress,
  type CatalogCollectionVideoItem,
  type CatalogResource,
} from '../api/catalog-api'
import { CatalogVideoPlayer } from './catalog-video-player'
import { KinescopeVideoPlayer } from './kinescope-video-player'
import { CatalogBackButton } from './catalog-back-button'

const viewerSectionTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.onboarding.titleDesktop),
}

const bodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: 'rgba(254, 254, 254, 0.9)',
  overflowWrap: 'anywhere' as const,
  wordBreak: 'break-word' as const,
  ...typographyStyle(designTokens.typography.body.md, {
    lineHeight: 'normal',
  }),
}

const actionTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.base),
}

const chipStyle = {
  height: '35px',
  borderRadius: '16px',
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} #404040`,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  padding: '9px 20px',
}

const chipTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  whiteSpace: 'nowrap' as const,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const actionButtonStyle = {
  height: '42px',
  borderRadius: '8px',
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} #404040`,
  padding: '9px 12px',
  gap: '4px',
}

const resourcesTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const resourcesLinkStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: '#A885FF',
  ...typographyStyle(designTokens.typography.body.base),
}

const resourcesCardTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.sm),
}

const responsiveViewerCardGridClass =
  'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'

const uniqueResources = (resources: CatalogResource[]) => {
  const byId = new Map<string, CatalogResource>()

  for (const resource of resources) {
    byId.set(resource.id, resource)
  }

  return [...byId.values()].sort((left, right) => left.position - right.position)
}

const summarizeApiError = (error: unknown) => {
  if (error instanceof ApiError) {
    return `Ошибка загрузки видео (${error.status})`
  }

  return 'Не удалось загрузить видео'
}

const formatDurationLabel = (seconds: number | null): string | undefined => {
  if (!seconds || seconds <= 0) {
    return undefined
  }

  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`
}

const toCollectionVideoRoute = (collectionSlug: string, itemSlug: string) =>
  `/catalog/collections/${collectionSlug}/videos/${itemSlug}`

const withViewerBrowseContext = (path: string, search: string) => {
  if (!search) {
    return path
  }

  return `${path}${search.startsWith('?') ? search : `?${search}`}`
}

const bookmarkKey = (itemKind: 'collection' | 'video', itemId: string) => `${itemKind}:${itemId}`
const VIEWER_QUERY_STALE_TIME_MS = 30_000
const PREFETCH_NEIGHBOR_WINDOW = 2
const COMMUNITY_BOT_URL = 'https://t.me/epicgrowth_com_bot'
const VIEWER_MAIN_SPACING_CLASS = 'px-6 pt-8'

const ChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 1.33C4.32 1.33 1.33 3.72 1.33 6.67C1.33 8.13 2.06 9.45 3.24 10.4V13.33L5.85 11.76C6.53 11.93 7.25 12 8 12C11.68 12 14.67 9.61 14.67 6.67C14.67 3.72 11.68 1.33 8 1.33Z"
      stroke="#FEFEFE"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
)

const BookmarkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.33398 4.66667C3.33398 3.19391 4.52789 2 6.00065 2H10.0007C11.4734 2 12.6673 3.19391 12.6673 4.66667V13.4456C12.6673 13.973 12.0838 14.2916 11.6401 14.0063L8.72166 12.1302C8.28245 11.8478 7.71885 11.8478 7.27964 12.1302L4.36116 14.0063C3.91748 14.2916 3.33398 13.973 3.33398 13.4456V4.66667Z"
      stroke="#FEFEFE"
      strokeWidth="1"
    />
    <path
      d="M6 5.66634H10"
      stroke="#FEFEFE"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export function VideoViewerPage() {
  const {
    collectionUrl,
    videoUrl,
    videoId: legacyVideoUrl,
  } = useParams<{
    collectionUrl?: string
    videoUrl?: string
    videoId?: string
  }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [bookmarkOverrides, setBookmarkOverrides] = useState<Record<string, boolean>>({})
  const [bookmarkPendingIds, setBookmarkPendingIds] = useState<Set<string>>(new Set())
  const prefetchedCollectionVideoKeysRef = useRef<Set<string>>(new Set())
  const lastProgressCommitRef = useRef<{
    key: string | null
    positionSeconds: number
    completed: boolean
    committedAtMs: number
  }>({
    key: null,
    positionSeconds: -1,
    completed: false,
    committedAtMs: 0,
  })

  const standaloneVideoUrl = !collectionUrl ? (videoUrl ?? legacyVideoUrl ?? null) : null
  const selectedCollectionVideoUrl = collectionUrl ? (videoUrl ?? null) : null
  const isCollectionOverviewMode = Boolean(collectionUrl && !selectedCollectionVideoUrl)
  const viewerBrowseContext = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const nextParams = new URLSearchParams()

    const favoritesOnly = params.get('favorites') === '1'
    if (favoritesOnly) {
      nextParams.set('favorites', '1')
    }

    const isBundlesSource = params.get('source') === 'bundles'
    if (isBundlesSource) {
      nextParams.set('source', 'bundles')
      const bundleId = params.get('bundle')
      if (bundleId) {
        nextParams.set('bundle', bundleId)
      }
    }

    const serialized = nextParams.toString()
    return {
      activeNavigationItem: favoritesOnly ? ('bookmarks' as const) : ('home' as const),
      search: serialized ? `?${serialized}` : '',
    }
  }, [location.search])

  useEffect(() => {
    if (collectionUrl || !legacyVideoUrl || videoUrl) {
      return
    }

    navigate(
      withViewerBrowseContext(`/catalog/videos/${legacyVideoUrl}`, viewerBrowseContext.search),
      {
        replace: true,
      }
    )
  }, [collectionUrl, legacyVideoUrl, navigate, videoUrl, viewerBrowseContext.search])

  const collectionQuery = useQuery({
    queryKey: ['catalog', 'collection', collectionUrl, selectedCollectionVideoUrl],
    enabled: Boolean(collectionUrl),
    placeholderData: (previousData) => previousData,
    staleTime: VIEWER_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchCatalogCollectionDetail({
        collectionUrl: collectionUrl as string,
        selectedVideoSlug: selectedCollectionVideoUrl,
      }),
  })

  const videoQuery = useQuery({
    queryKey: ['catalog', 'video', standaloneVideoUrl],
    enabled: Boolean(standaloneVideoUrl),
    placeholderData: (previousData) => previousData,
    staleTime: VIEWER_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    queryFn: () => fetchCatalogVideoDetail(standaloneVideoUrl as string),
  })

  useEffect(() => {
    if (collectionQuery.isFetching) {
      return
    }

    const redirectUrl = collectionQuery.data?.canonicalRedirectUrl
    if (!redirectUrl) {
      return
    }

    navigate(withViewerBrowseContext(redirectUrl, viewerBrowseContext.search), { replace: true })
  }, [
    collectionQuery.data?.canonicalRedirectUrl,
    collectionQuery.isFetching,
    navigate,
    viewerBrowseContext.search,
  ])

  useEffect(() => {
    if (
      !collectionUrl ||
      !videoUrl ||
      collectionQuery.isFetching ||
      collectionQuery.data?.canonicalRedirectUrl
    ) {
      return
    }

    const resolvedSelectedSlug = collectionQuery.data?.selectedVideo?.urlSlug ?? null
    if (!resolvedSelectedSlug || videoUrl === resolvedSelectedSlug) {
      return
    }

    queryClient.setQueryData(
      ['catalog', 'collection', collectionUrl, resolvedSelectedSlug],
      collectionQuery.data
    )
    navigate(
      withViewerBrowseContext(
        toCollectionVideoRoute(collectionUrl, resolvedSelectedSlug),
        viewerBrowseContext.search
      ),
      { replace: true }
    )
  }, [
    collectionQuery.data?.canonicalRedirectUrl,
    collectionQuery.data,
    collectionQuery.data?.selectedVideo?.urlSlug,
    collectionQuery.isFetching,
    collectionUrl,
    navigate,
    videoUrl,
    viewerBrowseContext.search,
  ])

  const isCollectionMode = Boolean(collectionUrl)
  const activeQuery = isCollectionMode ? collectionQuery : videoQuery
  const hasResolvedData = Boolean(collectionQuery.data || videoQuery.data)
  const isLoading = activeQuery.isPending && !hasResolvedData
  const hasError = activeQuery.isError
  const errorMessage = summarizeApiError(activeQuery.error)

  const collectionData = collectionQuery.data
  const standaloneVideoData = videoQuery.data

  const selectedVideo = collectionData?.selectedVideo ?? null
  const standaloneVideo = standaloneVideoData?.video ?? null
  const isRouteDataResolved = isCollectionMode
    ? !selectedCollectionVideoUrl || selectedVideo?.urlSlug === selectedCollectionVideoUrl
    : !standaloneVideoUrl || standaloneVideo?.urlSlug === standaloneVideoUrl

  const primaryTitle = isCollectionOverviewMode
    ? (collectionData?.collection.title ?? null)
    : (selectedVideo?.title ?? standaloneVideo?.title ?? collectionData?.collection.title ?? null)

  const primaryDescription = useMemo(() => {
    if (isCollectionOverviewMode) {
      return {
        html: collectionData?.collection.descriptionHtml ?? null,
        text: collectionData?.collection.shortDescription ?? null,
      }
    }

    if (isCollectionMode) {
      return {
        html: selectedVideo?.descriptionHtml ?? collectionData?.collection.descriptionHtml ?? null,
        text:
          selectedVideo?.shortDescription ?? collectionData?.collection.shortDescription ?? null,
      }
    }

    return {
      html: standaloneVideo?.descriptionHtml ?? null,
      text: standaloneVideo?.shortDescription ?? null,
    }
  }, [collectionData, isCollectionMode, isCollectionOverviewMode, selectedVideo, standaloneVideo])

  const primaryDescriptionHtml =
    primaryDescription && hasMeaningfulHtml(primaryDescription.html)
      ? primaryDescription.html
      : null
  const primaryDescriptionText =
    !primaryDescriptionHtml && primaryDescription ? primaryDescription.text : null

  const playbackUrl =
    selectedVideo?.playback?.videoUrl ?? standaloneVideo?.playback?.videoUrl ?? null

  // Ролик есть в кинескопе — играем им. Пусто — остаётся прежний путь через
  // подписанную ссылку на хранилище: переклейка идёт построчно и обратима без
  // выката.
  const kinescopeId =
    selectedVideo?.playback?.kinescopeId ?? standaloneVideo?.playback?.kinescopeId ?? null

  const playbackKey =
    selectedVideo?.id ??
    standaloneVideo?.id ??
    collectionData?.collection.id ??
    standaloneVideoData?.video.id ??
    null

  // Signed playback URLs are embedded in the query payload, so when React Query
  // serves cached viewer data and immediately refetches in the background, we
  // should not bind the cached signed URL to the player until the refresh
  // finishes. Otherwise the player can open with an already-invalid signature
  // and only recover after a full page reload.
  const shouldHoldPlaybackWhileRefreshing =
    Boolean(playbackUrl) &&
    isRouteDataResolved &&
    activeQuery.isFetching &&
    activeQuery.dataUpdatedAt > 0
  const effectivePlaybackUrl = shouldHoldPlaybackWhileRefreshing ? null : playbackUrl
  const hasPlayableVideo = Boolean((effectivePlaybackUrl || kinescopeId) && playbackKey)
  const isPlaybackTransition = !isRouteDataResolved || activeQuery.isFetching
  const playerInstanceKey =
    playbackKey ?? (isCollectionMode ? 'collection-fallback' : 'video-fallback')
  const refreshPlaybackSource = useCallback(() => {
    if (isCollectionMode) {
      void collectionQuery.refetch()
      return
    }

    void videoQuery.refetch()
  }, [collectionQuery, isCollectionMode, videoQuery])

  const collectionVideos = useMemo(
    () => (collectionData?.videos ?? []).map((item) => ({ ...item })),
    [collectionData?.videos]
  )

  const toCollectionLessonRoute = useCallback(
    (item: CatalogCollectionVideoItem) => {
      if (!collectionUrl) {
        return item.openUrl
      }

      if (item.access.state === 'locked') {
        return item.openUrl
      }

      return toCollectionVideoRoute(collectionUrl, item.urlSlug)
    },
    [collectionUrl]
  )

  const collectionOverviewStartRoute = useMemo(() => {
    if (!isCollectionOverviewMode || !collectionUrl || collectionVideos.length === 0) {
      return null
    }

    if (collectionData?.resume?.openUrl) {
      return collectionData.resume.openUrl
    }

    if (collectionData?.resume?.videoUrlSlug) {
      return toCollectionVideoRoute(collectionUrl, collectionData.resume.videoUrlSlug)
    }

    const availableItem = collectionVideos.find((item) => item.access.state === 'available')
    if (availableItem) {
      return toCollectionVideoRoute(collectionUrl, availableItem.urlSlug)
    }

    const partiallyAvailableItem = collectionVideos.find((item) => item.access.state === 'partial')
    if (partiallyAvailableItem) {
      return toCollectionLessonRoute(partiallyAvailableItem)
    }

    const fallbackOpenItem = collectionVideos.find((item) => Boolean(item.openUrl))
    if (!fallbackOpenItem) {
      return null
    }

    return toCollectionLessonRoute(fallbackOpenItem)
  }, [
    collectionData?.resume?.openUrl,
    collectionData?.resume?.videoUrlSlug,
    collectionUrl,
    collectionVideos,
    isCollectionOverviewMode,
    toCollectionLessonRoute,
  ])

  useEffect(() => {
    prefetchedCollectionVideoKeysRef.current.clear()
  }, [collectionUrl])

  useEffect(() => {
    if (!collectionUrl || collectionVideos.length === 0) {
      return
    }

    const selectedSlug =
      selectedVideo?.urlSlug ?? selectedCollectionVideoUrl ?? collectionVideos[0]?.urlSlug ?? null
    if (!selectedSlug) {
      return
    }

    const selectedIndex = collectionVideos.findIndex((item) => item.urlSlug === selectedSlug)
    if (selectedIndex < 0) {
      return
    }

    const prefetchTargets = new Set<number>()
    for (
      let index = Math.max(0, selectedIndex - PREFETCH_NEIGHBOR_WINDOW);
      index <= Math.min(collectionVideos.length - 1, selectedIndex + PREFETCH_NEIGHBOR_WINDOW);
      index += 1
    ) {
      if (index === selectedIndex) {
        continue
      }
      prefetchTargets.add(index)
    }

    for (const index of prefetchTargets) {
      const item = collectionVideos[index]
      if (!item || item.access.state !== 'available') {
        continue
      }

      const cacheKey = `${collectionUrl}:${item.urlSlug}`
      if (prefetchedCollectionVideoKeysRef.current.has(cacheKey)) {
        continue
      }

      prefetchedCollectionVideoKeysRef.current.add(cacheKey)
      void queryClient.prefetchQuery({
        queryKey: ['catalog', 'collection', collectionUrl, item.urlSlug],
        staleTime: VIEWER_QUERY_STALE_TIME_MS,
        queryFn: () =>
          fetchCatalogCollectionDetail({
            collectionUrl,
            selectedVideoSlug: item.urlSlug,
          }),
      })
    }
  }, [collectionUrl, collectionVideos, selectedCollectionVideoUrl, selectedVideo?.urlSlug])

  const prefetchCollectionVideo = useCallback(
    (item: CatalogCollectionVideoItem) => {
      if (!collectionUrl || item.access.state !== 'available') {
        return
      }

      const cacheKey = `${collectionUrl}:${item.urlSlug}`
      if (prefetchedCollectionVideoKeysRef.current.has(cacheKey)) {
        return
      }

      prefetchedCollectionVideoKeysRef.current.add(cacheKey)
      void queryClient.prefetchQuery({
        queryKey: ['catalog', 'collection', collectionUrl, item.urlSlug],
        staleTime: VIEWER_QUERY_STALE_TIME_MS,
        queryFn: () =>
          fetchCatalogCollectionDetail({
            collectionUrl,
            selectedVideoSlug: item.urlSlug,
          }),
      })
    },
    [collectionUrl]
  )

  const mergedResources = useMemo(() => {
    if (isCollectionMode) {
      return uniqueResources([
        ...(collectionData?.resources ?? []),
        ...(selectedVideo?.resources ?? []),
      ])
    }

    return uniqueResources(standaloneVideoData?.resources ?? [])
  }, [
    collectionData?.resources,
    isCollectionMode,
    selectedVideo?.resources,
    standaloneVideoData?.resources,
  ])

  const selectedCategories = useMemo(() => {
    if (isCollectionOverviewMode) {
      return collectionData?.collection.categories ?? []
    }

    if (isCollectionMode) {
      if (selectedVideo?.categories?.length) {
        return selectedVideo.categories
      }
      return collectionData?.collection.categories ?? []
    }

    return standaloneVideo?.categories ?? []
  }, [
    collectionData?.collection.categories,
    isCollectionMode,
    isCollectionOverviewMode,
    selectedVideo?.categories,
    standaloneVideo?.categories,
  ])

  const selectedPlaybackProgressSeconds = useMemo(() => {
    if (isCollectionMode) {
      const resumeVideoId = collectionData?.resume?.videoId
      const resumePositionSeconds = collectionData?.resume?.positionSeconds ?? null
      const selectedVideoId = selectedVideo?.id
      if (resumeVideoId && selectedVideoId && resumeVideoId === selectedVideoId) {
        return resumePositionSeconds
      }
      return selectedVideo?.playbackProgressSeconds ?? null
    }

    return standaloneVideo?.playbackProgressSeconds ?? null
  }, [
    collectionData?.resume?.positionSeconds,
    collectionData?.resume?.videoId,
    isCollectionMode,
    selectedVideo?.id,
    selectedVideo?.playbackProgressSeconds,
    standaloneVideo?.playbackProgressSeconds,
  ])

  useEffect(() => {
    setBookmarkOverrides({})
    setBookmarkPendingIds(new Set())
  }, [collectionData, standaloneVideoData])

  const isBookmarked = useCallback(
    (itemKind: 'collection' | 'video', itemId: string, fallback: boolean) =>
      bookmarkOverrides[bookmarkKey(itemKind, itemId)] ?? fallback,
    [bookmarkOverrides]
  )

  const toggleBookmark = useCallback(
    async (input: { itemKind: 'collection' | 'video'; itemId: string; current: boolean }) => {
      const key = bookmarkKey(input.itemKind, input.itemId)
      if (bookmarkPendingIds.has(key)) {
        return
      }

      const nextBookmarked = !input.current
      const previousCatalogCache = captureCatalogCacheSnapshots()

      setBookmarkPendingIds((current) => new Set(current).add(key))
      setBookmarkOverrides((current) => ({
        ...current,
        [key]: nextBookmarked,
      }))
      applyBookmarkToCatalogCache({
        itemKind: input.itemKind,
        itemId: input.itemId,
        bookmarked: nextBookmarked,
      })

      try {
        await setCatalogFavorite({
          itemKind: input.itemKind,
          itemId: input.itemId,
          bookmarked: nextBookmarked,
        })
      } catch {
        setBookmarkOverrides((current) => ({
          ...current,
          [key]: input.current,
        }))
        restoreCatalogCacheSnapshots(previousCatalogCache)
      } finally {
        setBookmarkPendingIds((current) => {
          const next = new Set(current)
          next.delete(key)
          return next
        })
        void queryClient.invalidateQueries({
          predicate: (query) => isCatalogListQueryKey(query.queryKey),
        })
      }
    },
    [bookmarkPendingIds]
  )

  const handlePlaybackCheckpoint = useCallback(
    (input: { positionSeconds: number; durationSeconds: number | null; completed: boolean }) => {
      const activeVideoId = selectedVideo?.id ?? standaloneVideo?.id ?? null
      if (!activeVideoId) {
        return
      }

      const payloadKey = `${collectionData?.collection.id ?? 'standalone'}:${activeVideoId}`
      const nowMs = Date.now()
      const roundedPosition = Math.max(0, Math.floor(input.positionSeconds))
      const previous = lastProgressCommitRef.current
      const unchanged =
        previous.key === payloadKey &&
        previous.positionSeconds === roundedPosition &&
        previous.completed === input.completed
      const recentCommit =
        previous.key === payloadKey && nowMs - previous.committedAtMs < 4_000 && !input.completed

      if (unchanged || recentCommit) {
        return
      }

      lastProgressCommitRef.current = {
        key: payloadKey,
        positionSeconds: roundedPosition,
        completed: input.completed,
        committedAtMs: nowMs,
      }

      void setCatalogPlaybackProgress({
        videoId: activeVideoId,
        collectionId: collectionData?.collection.id ?? null,
        positionSeconds: roundedPosition,
        durationSeconds: input.durationSeconds,
        completed: input.completed,
      }).catch(() => undefined)
    },
    [collectionData?.collection.id, selectedVideo?.id, standaloneVideo?.id]
  )

  const primaryBookmarkTarget = useMemo(() => {
    if (isCollectionOverviewMode && collectionData?.collection) {
      return {
        itemKind: 'collection' as const,
        itemId: collectionData.collection.id,
        current: isBookmarked(
          'collection',
          collectionData.collection.id,
          collectionData.collection.isBookmarked
        ),
      }
    }

    if (selectedVideo) {
      return {
        itemKind: 'video' as const,
        itemId: selectedVideo.id,
        current: isBookmarked('video', selectedVideo.id, selectedVideo.isBookmarked),
      }
    }

    if (standaloneVideo) {
      return {
        itemKind: 'video' as const,
        itemId: standaloneVideo.id,
        current: isBookmarked('video', standaloneVideo.id, standaloneVideo.isBookmarked),
      }
    }

    if (collectionData?.collection) {
      return {
        itemKind: 'collection' as const,
        itemId: collectionData.collection.id,
        current: isBookmarked(
          'collection',
          collectionData.collection.id,
          collectionData.collection.isBookmarked
        ),
      }
    }

    return null
  }, [
    collectionData?.collection,
    isBookmarked,
    isCollectionOverviewMode,
    selectedVideo,
    standaloneVideo,
  ])
  const primaryBookmarkPending = primaryBookmarkTarget
    ? bookmarkPendingIds.has(
        bookmarkKey(primaryBookmarkTarget.itemKind, primaryBookmarkTarget.itemId)
      )
    : false

  const handleNavigationSelect = useCallback(
    (itemId: 'home' | 'bookmarks' | 'subscription') => {
      if (itemId === 'subscription') {
        navigate(routes.catalogSubscription)
        return
      }

      if (itemId === 'bookmarks') {
        navigate('/catalog?favorites=1')
        return
      }
      navigate('/catalog')
    },
    [navigate]
  )

  const handleBackNavigation = useCallback(() => {
    if (isCollectionOverviewMode) {
      navigate('/catalog')
      return
    }

    if (isCollectionMode && collectionUrl) {
      navigate(`/catalog/collections/${collectionUrl}`)
      return
    }

    navigate('/catalog')
  }, [collectionUrl, isCollectionMode, isCollectionOverviewMode, navigate])

  const handleLogout = useCallback(async () => {
    await logout()
    navigate(routes.registration, { replace: true })
  }, [logout, navigate])

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: designTokens.colors.neutral.black }}>
        <VideosShell
          activeNavigationItem={viewerBrowseContext.activeNavigationItem}
          onNavigationSelect={handleNavigationSelect}
          currentPathname={location.pathname}
          fullHeight={false}
          showSearchControl={false}
          showFilterControls={false}
          onLogout={() => {
            void handleLogout()
          }}
        >
          <main
            className={cn(
              'flex min-h-0 w-full flex-1 items-center justify-center',
              VIEWER_MAIN_SPACING_CLASS
            )}
          >
            <p style={bodyStyle} className="text-center">
              Загружаем видео...
            </p>
          </main>
        </VideosShell>
      </div>
    )
  }

  if (hasError) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: designTokens.colors.neutral.black }}>
        <VideosShell
          activeNavigationItem={viewerBrowseContext.activeNavigationItem}
          onNavigationSelect={handleNavigationSelect}
          currentPathname={location.pathname}
          fullHeight={false}
          showSearchControl={false}
          showFilterControls={false}
          onLogout={() => {
            void handleLogout()
          }}
        >
          <main
            className={cn(
              'flex min-h-0 w-full flex-1 items-center justify-center',
              VIEWER_MAIN_SPACING_CLASS
            )}
          >
            <p style={bodyStyle} className="text-center">
              {errorMessage}
            </p>
          </main>
        </VideosShell>
      </div>
    )
  }

  if (!primaryTitle) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: designTokens.colors.neutral.black }}>
        <VideosShell
          activeNavigationItem={viewerBrowseContext.activeNavigationItem}
          onNavigationSelect={handleNavigationSelect}
          currentPathname={location.pathname}
          fullHeight={false}
          showSearchControl={false}
          showFilterControls={false}
          onLogout={() => {
            void handleLogout()
          }}
        >
          <main
            className={cn(
              'flex min-h-0 w-full flex-1 items-center justify-center',
              VIEWER_MAIN_SPACING_CLASS
            )}
          >
            <p style={bodyStyle} className="text-center">
              Видео пока недоступно.
            </p>
          </main>
        </VideosShell>
      </div>
    )
  }

  if (isCollectionOverviewMode && collectionData?.collection) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: designTokens.colors.neutral.black }}>
        <VideosShell
          activeNavigationItem={viewerBrowseContext.activeNavigationItem}
          onNavigationSelect={handleNavigationSelect}
          currentPathname={location.pathname}
          fullHeight={false}
          showSearchControl={false}
          showFilterControls={false}
          onLogout={() => {
            void handleLogout()
          }}
        >
          <main
            className={cn(
              'flex min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto',
              VIEWER_MAIN_SPACING_CLASS
            )}
          >
            <div className="mx-auto flex w-full max-w-[1320px] min-w-0 flex-col gap-8 pb-16 lg:pb-24">
              <CatalogBackButton onClick={handleBackNavigation} />
              <section className="flex w-full flex-col gap-6 p-0">
                <div className="flex w-full flex-col gap-4">
                  <h1 style={viewerSectionTitleStyle}>{collectionData.collection.title}</h1>
                  {selectedCategories.length > 0 ? (
                    <div className="flex w-full flex-wrap items-center gap-3">
                      {selectedCategories.map((category) => (
                        <button
                          key={`overview-category-${category.id}`}
                          type="button"
                          className="inline-flex shrink-0 cursor-pointer items-center justify-center transition-colors hover:bg-[#303030]"
                          style={chipStyle}
                          onClick={() =>
                            navigate(`/catalog?category=${encodeURIComponent(category.urlSlug)}`)
                          }
                        >
                          <span style={chipTextStyle}>{category.title}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {primaryDescriptionHtml ? (
                  <HtmlContent
                    html={primaryDescriptionHtml}
                    style={bodyStyle}
                    className="min-w-0 max-w-full"
                    data-testid="viewer-description-html"
                  />
                ) : primaryDescriptionText ? (
                  <p className="min-w-0 max-w-full" style={bodyStyle}>
                    {primaryDescriptionText}
                  </p>
                ) : null}

                <div className="flex w-full flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center justify-center rounded-[8px] bg-[#FF28BE] px-6 py-2.5 text-sm font-semibold text-[#FEFEFE] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={!collectionOverviewStartRoute}
                    onClick={() => {
                      if (!collectionOverviewStartRoute) {
                        return
                      }

                      navigate(
                        withViewerBrowseContext(
                          collectionOverviewStartRoute,
                          viewerBrowseContext.search
                        )
                      )
                    }}
                  >
                    Смотреть
                  </button>

                  <a
                    href={COMMUNITY_BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex cursor-pointer items-center justify-center no-underline"
                    style={actionButtonStyle}
                    aria-label="Обсудить в комьюнити в Telegram"
                  >
                    <ChatIcon />
                    <span style={actionTextStyle}>Обсудить в комьюнити</span>
                  </a>

                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center justify-center"
                    style={{
                      ...actionButtonStyle,
                      backgroundColor: primaryBookmarkTarget?.current ? '#404040' : 'transparent',
                      borderColor: '#404040',
                      opacity: primaryBookmarkPending ? 0.72 : 1,
                    }}
                    disabled={!primaryBookmarkTarget || primaryBookmarkPending}
                    aria-busy={primaryBookmarkPending}
                    onClick={() => {
                      if (!primaryBookmarkTarget) {
                        return
                      }
                      void toggleBookmark(primaryBookmarkTarget)
                    }}
                  >
                    <BookmarkIcon />
                    <span style={actionTextStyle}>
                      {primaryBookmarkPending
                        ? primaryBookmarkTarget?.current
                          ? 'Удаляем...'
                          : 'Сохраняем...'
                        : primaryBookmarkTarget?.current
                          ? 'Сохранено'
                          : 'Сохранить'}
                    </span>
                  </button>
                </div>
              </section>

              <section className="flex w-full flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 style={resourcesTitleStyle}>Материалы</h2>
                </div>

                {collectionVideos.length > 0 ? (
                  <div className={responsiveViewerCardGridClass}>
                    {collectionVideos.map((item, index) => {
                      const nextRoute = toCollectionLessonRoute(item)

                      return (
                        <VideoCard
                          key={`overview-lesson-${item.id}`}
                          className="w-full! max-w-none"
                          coverSrc={item.coverUrl ?? catalogPlaceholderCover}
                          title={item.title}
                          shortDescription={item.shortDescription}
                          contentTypeLabel="Видео"
                          contentCountLabel={`Урок ${index + 1}`}
                          durationLabel={formatDurationLabel(item.durationSeconds)}
                          accessState={item.access.state}
                          isBookmarked={isBookmarked('video', item.id, item.isBookmarked)}
                          isBookmarkPending={bookmarkPendingIds.has(bookmarkKey('video', item.id))}
                          onHoverStart={() => {
                            prefetchCollectionVideo(item)
                          }}
                          onBookmarkToggle={() => {
                            void toggleBookmark({
                              itemKind: 'video',
                              itemId: item.id,
                              current: isBookmarked('video', item.id, item.isBookmarked),
                            })
                          }}
                          onClick={
                            nextRoute
                              ? () =>
                                  navigate(
                                    withViewerBrowseContext(nextRoute, viewerBrowseContext.search)
                                  )
                              : undefined
                          }
                        />
                      )
                    })}
                  </div>
                ) : (
                  <p style={bodyStyle}>В этой коллекции пока нет уроков.</p>
                )}
              </section>
            </div>
          </main>
        </VideosShell>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: designTokens.colors.neutral.black }}>
      <VideosShell
        activeNavigationItem={viewerBrowseContext.activeNavigationItem}
        onNavigationSelect={handleNavigationSelect}
        currentPathname={location.pathname}
        fullHeight={false}
        showSearchControl={false}
        showFilterControls={false}
        onLogout={() => {
          void handleLogout()
        }}
      >
        <main
          className={cn(
            'flex min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto',
            VIEWER_MAIN_SPACING_CLASS
          )}
        >
          <div className="mx-auto flex w-full max-w-[1320px] min-w-0 gap-6 pb-16 lg:pb-24">
            <section className="flex min-w-0 flex-1 flex-col gap-6">
              <CatalogBackButton onClick={handleBackNavigation} />
              <div className="relative w-full shrink-0 overflow-hidden rounded-[12px] border border-[#404040] bg-[#121212] aspect-video">
                {hasPlayableVideo ? (
                  <>
                    {kinescopeId ? (
                      <KinescopeVideoPlayer
                        key={`kinescope:${playerInstanceKey}`}
                        kinescopeId={kinescopeId}
                        poster={
                          selectedVideo?.coverUrl ??
                          standaloneVideo?.coverUrl ??
                          collectionData?.collection.coverUrl
                        }
                        title={primaryTitle}
                        initialProgressSeconds={selectedPlaybackProgressSeconds}
                        onPlaybackError={refreshPlaybackSource}
                        onPlaybackCheckpoint={handlePlaybackCheckpoint}
                      />
                    ) : (
                    <CatalogVideoPlayer
                      key={playerInstanceKey}
                      src={effectivePlaybackUrl ?? ''}
                      poster={
                        selectedVideo?.coverUrl ??
                        standaloneVideo?.coverUrl ??
                        collectionData?.collection.coverUrl
                      }
                      title={primaryTitle}
                      initialProgressSeconds={selectedPlaybackProgressSeconds}
                      onPlaybackError={refreshPlaybackSource}
                      onPlaybackCheckpoint={handlePlaybackCheckpoint}
                    />
                    )}
                    {isPlaybackTransition ? (
                      <div className="pointer-events-none absolute right-4 top-4 z-20 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-white/90" />
                        <span className="text-xs text-white/90">Переключаем видео...</span>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="absolute inset-0 flex w-full items-center justify-center bg-[#0A0A0A] px-4">
                    <p style={bodyStyle} className="text-center">
                      {isPlaybackTransition
                        ? 'Загружаем видео...'
                        : 'Видео пока недоступно. Для этого материала нет доступа или источник ещё не готов.'}
                    </p>
                  </div>
                )}
              </div>

              <section className="flex w-full flex-col gap-6">
                <div className="flex w-full flex-col gap-4">
                  <h1 style={viewerSectionTitleStyle}>{primaryTitle}</h1>
                  {selectedCategories.length > 0 ? (
                    <div className="flex w-full flex-wrap items-center gap-3">
                      {selectedCategories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          className="inline-flex cursor-pointer items-center justify-center transition-colors hover:bg-[#303030]"
                          style={chipStyle}
                          onClick={() =>
                            navigate(`/catalog?category=${encodeURIComponent(category.urlSlug)}`)
                          }
                        >
                          <span style={chipTextStyle}>{category.title}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {primaryDescriptionHtml ? (
                  <HtmlContent
                    html={primaryDescriptionHtml}
                    style={bodyStyle}
                    className="min-w-0 max-w-full"
                    data-testid="viewer-description-html"
                  />
                ) : primaryDescriptionText ? (
                  <p className="min-w-0 max-w-full" style={bodyStyle}>
                    {primaryDescriptionText}
                  </p>
                ) : null}

                <div className="flex w-full flex-wrap items-center gap-3">
                  <a
                    href={COMMUNITY_BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex cursor-pointer items-center justify-center no-underline"
                    style={actionButtonStyle}
                    aria-label="Обсудить в комьюнити в Telegram"
                  >
                    <ChatIcon />
                    <span style={actionTextStyle}>Обсудить в комьюнити</span>
                  </a>
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center justify-center"
                    style={{
                      ...actionButtonStyle,
                      backgroundColor: primaryBookmarkTarget?.current ? '#404040' : 'transparent',
                      borderColor: '#404040',
                      opacity: primaryBookmarkPending ? 0.72 : 1,
                    }}
                    disabled={!primaryBookmarkTarget || primaryBookmarkPending}
                    aria-busy={primaryBookmarkPending}
                    onClick={() => {
                      if (!primaryBookmarkTarget) {
                        return
                      }
                      void toggleBookmark(primaryBookmarkTarget)
                    }}
                  >
                    <BookmarkIcon />
                    <span style={actionTextStyle}>
                      {primaryBookmarkPending
                        ? primaryBookmarkTarget?.current
                          ? 'Удаляем...'
                          : 'Сохраняем...'
                        : primaryBookmarkTarget?.current
                          ? 'Сохранено'
                          : 'Сохранить'}
                    </span>
                  </button>
                </div>

                {mergedResources.length > 0 ? (
                  <section className="flex w-full flex-col gap-3 rounded-[12px] border border-[#404040] bg-[#1A1A1A] p-4">
                    <h2 style={resourcesTitleStyle}>Дополнительные материалы</h2>
                    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {mergedResources.map((resource) => (
                        <li key={resource.id} className="min-w-0">
                          {resource.fileUrl ? (
                            <a
                              href={resource.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={resourcesLinkStyle}
                              className="flex min-h-[72px] w-full flex-col justify-between rounded-[8px] border border-[#404040] bg-[#232323] p-3 hover:opacity-80"
                            >
                              <span style={resourcesCardTitleStyle} className="line-clamp-2">
                                {resource.title || resource.fileName}
                              </span>
                              <span className="mt-2 text-xs text-[#FEFEFEB2]">Открыть файл</span>
                            </a>
                          ) : (
                            <span
                              style={bodyStyle}
                              className="flex min-h-[72px] w-full items-center rounded-[8px] border border-[#404040] bg-[#232323] p-3"
                            >
                              {resource.title || resource.fileName}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </section>
            </section>

            {isCollectionMode && collectionVideos.length > 0 ? (
              <aside className="sticky top-6 hidden h-[calc(100vh-140px)] w-[292px] shrink-0 flex-col gap-3 overflow-x-hidden overflow-y-auto pr-1 lg:flex">
                {collectionVideos.map((item: CatalogCollectionVideoItem) => {
                  const isSelected = item.id === selectedVideo?.id
                  const canOpen = item.access.state === 'available' && Boolean(collectionUrl)
                  const nextRoute =
                    canOpen && collectionUrl
                      ? toCollectionVideoRoute(collectionUrl, item.urlSlug)
                      : item.openUrl

                  return (
                    <VideoCard
                      key={`rail-${item.id}`}
                      coverSrc={item.coverUrl ?? catalogPlaceholderCover}
                      title={item.title}
                      shortDescription={item.shortDescription}
                      contentTypeLabel="Видео"
                      durationLabel={formatDurationLabel(item.durationSeconds)}
                      accessState={item.access.state}
                      isSelected={isSelected}
                      isBookmarked={isBookmarked('video', item.id, item.isBookmarked)}
                      isBookmarkPending={bookmarkPendingIds.has(bookmarkKey('video', item.id))}
                      onBookmarkToggle={() => {
                        void toggleBookmark({
                          itemKind: 'video',
                          itemId: item.id,
                          current: isBookmarked('video', item.id, item.isBookmarked),
                        })
                      }}
                      onHoverStart={() => {
                        prefetchCollectionVideo(item)
                      }}
                      onClick={
                        nextRoute
                          ? () =>
                              navigate(
                                withViewerBrowseContext(nextRoute, viewerBrowseContext.search)
                              )
                          : undefined
                      }
                    />
                  )
                })}
              </aside>
            ) : null}
          </div>
        </main>
      </VideosShell>
    </div>
  )
}

VideoViewerPage.displayName = 'VideoViewerPage'
const catalogPlaceholderCover = '/no-image.png'
